import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../../contexts/AuthContext';
import { apiClient } from '../../../../services/api';
import { useToast } from '../../../../contexts/ToastContext';
import { Plan } from '../../../../types/domain';

export interface AdminPlanResponse extends Plan {
  organizations_count?: number;
}

export function useAdminPlans() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [plans, setPlans] = useState<AdminPlanResponse[]>([]);

  // Create / Edit Modal
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPlanId, setEditingPlanId] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [description, setDescription] = useState('');
  const [maxMonthlySubmissions, setMaxMonthlySubmissions] = useState(500);
  const [maxTables, setMaxTables] = useState(3);
  const [maxTeamSeats, setMaxTeamSeats] = useState(2);
  const [retentionDays, setRetentionDays] = useState(30);
  const [maxStorageMB, setMaxStorageMB] = useState(100);
  const [maxFileSizeMB, setMaxFileSizeMB] = useState(10);
  const [priceMonthlyDollars, setPriceMonthlyDollars] = useState(0);
  const [priceYearlyDollars, setPriceYearlyDollars] = useState(0);
  const [isActive, setIsActive] = useState(true);
  const [isDefault, setIsDefault] = useState(false);
  const [position, setPosition] = useState(1);
  const [featuresText, setFeaturesText] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // Guard: Superadmin only
  useEffect(() => {
    if (user && !user.is_superadmin) {
      showToast({
        type: 'error',
        title: 'Access Denied',
        message: 'Platform super-admin permissions required to manage subscription tiers.',
      });
      navigate('/dashboard', { replace: true });
    }
  }, [user, navigate, showToast]);

  const fetchPlans = useCallback(async () => {
    try {
      const res = await apiClient<{ data: AdminPlanResponse[] }>('/admin/plans');
      setPlans(res.data || []);
    } catch (err: any) {
      showToast({
        type: 'error',
        title: 'Failed to Load Plans',
        message: err?.message || 'Could not retrieve subscription plans',
      });
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, [showToast]);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  const handleRefresh = () => {
    setIsRefreshing(true);
    fetchPlans();
  };

  const handleOpenCreate = () => {
    setEditingPlanId(null);
    setName('');
    setSlug('');
    setDescription('');
    setMaxMonthlySubmissions(5000);
    setMaxTables(10);
    setMaxTeamSeats(5);
    setRetentionDays(60);
    setMaxStorageMB(1024);
    setMaxFileSizeMB(10);
    setPriceMonthlyDollars(19);
    setPriceYearlyDollars(190);
    setIsActive(true);
    setIsDefault(false);
    setPosition((plans.length || 0) + 1);
    setFeaturesText(
      '5,000 monthly submissions\n10 datastore tables\n5 team seats\n1 GB total storage\n10 MB max file size\n60-day data retention'
    );
    setIsModalOpen(true);
  };

  const handleOpenEdit = (plan: AdminPlanResponse) => {
    setEditingPlanId(plan.id);
    setName(plan.name);
    setSlug(plan.slug);
    setDescription(plan.description || '');
    setMaxMonthlySubmissions(plan.max_monthly_submissions);
    setMaxTables(plan.max_tables);
    setMaxTeamSeats(plan.max_team_seats);
    setRetentionDays(plan.retention_days);
    setMaxStorageMB(Math.round((plan.max_storage_bytes || 104857600) / (1024 * 1024)));
    setMaxFileSizeMB(Math.round((plan.max_file_size_bytes || 10485760) / (1024 * 1024)));
    setPriceMonthlyDollars(Math.round((plan.price_monthly_cents || 0) / 100));
    setPriceYearlyDollars(Math.round((plan.price_yearly_cents || 0) / 100));
    setIsActive(plan.is_active);
    setIsDefault(plan.is_default);
    setPosition(plan.position || 0);

    let featArr: string[] = [];
    if (Array.isArray(plan.features)) {
      featArr = plan.features;
    } else if (typeof plan.features === 'string') {
      try {
        featArr = JSON.parse(plan.features);
      } catch {
        featArr = [];
      }
    }
    setFeaturesText(featArr.join('\n'));
    setIsModalOpen(true);
  };

  const handleSavePlan = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !slug.trim()) {
      showToast({ type: 'error', title: 'Validation Error', message: 'Name and slug are required' });
      return;
    }

    const features = featuresText
      .split('\n')
      .map((f) => f.trim())
      .filter(Boolean);

    const payload = {
      name: name.trim(),
      slug: slug.trim().toLowerCase(),
      description: description.trim(),
      max_monthly_submissions: Number(maxMonthlySubmissions),
      max_tables: Number(maxTables),
      max_team_seats: Number(maxTeamSeats),
      retention_days: Number(retentionDays),
      max_storage_bytes: Math.round(Number(maxStorageMB) * 1024 * 1024),
      max_file_size_bytes: Math.round(Number(maxFileSizeMB) * 1024 * 1024),
      price_monthly_cents: Math.round(Number(priceMonthlyDollars) * 100),
      price_yearly_cents: Math.round(Number(priceYearlyDollars) * 100),
      is_active: isActive,
      is_default: isDefault,
      position: Number(position),
      features,
    };

    setIsSaving(true);
    try {
      if (editingPlanId) {
        await apiClient(`/admin/plans/${editingPlanId}`, {
          method: 'PUT',
          body: JSON.stringify(payload),
        });
        showToast({
          type: 'success',
          title: 'Plan Updated',
          message: `Plan "${name}" updated successfully.`,
        });
      } else {
        await apiClient('/admin/plans', {
          method: 'POST',
          body: JSON.stringify(payload),
        });
        showToast({
          type: 'success',
          title: 'Plan Created',
          message: `Plan "${name}" created successfully.`,
        });
      }
      setIsModalOpen(false);
      fetchPlans();
    } catch (err: any) {
      showToast({
        type: 'error',
        title: 'Save Failed',
        message: err?.message || 'Could not save plan configuration',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeletePlan = async (plan: AdminPlanResponse) => {
    const confirmMsg = `Are you sure you want to delete plan "${plan.name}"?`;
    if (!window.confirm(confirmMsg)) return;

    try {
      await apiClient(`/admin/plans/${plan.id}`, {
        method: 'DELETE',
      });
      showToast({
        type: 'success',
        title: 'Plan Deleted',
        message: `Plan "${plan.name}" removed successfully.`,
      });
      fetchPlans();
    } catch (err: any) {
      showToast({
        type: 'error',
        title: 'Delete Failed',
        message: err?.message || 'Could not delete plan',
      });
    }
  };

  return {
    plans,
    isLoading,
    isRefreshing,
    handleRefresh,
    isModalOpen,
    setIsModalOpen,
    editingPlanId,
    name,
    setName,
    slug,
    setSlug,
    description,
    setDescription,
    maxMonthlySubmissions,
    setMaxMonthlySubmissions,
    maxTables,
    setMaxTables,
    maxTeamSeats,
    setMaxTeamSeats,
    retentionDays,
    setRetentionDays,
    maxStorageMB,
    setMaxStorageMB,
    maxFileSizeMB,
    setMaxFileSizeMB,
    priceMonthlyDollars,
    setPriceMonthlyDollars,
    priceYearlyDollars,
    setPriceYearlyDollars,
    isActive,
    setIsActive,
    isDefault,
    setIsDefault,
    position,
    setPosition,
    featuresText,
    setFeaturesText,
    isSaving,
    handleOpenCreate,
    handleOpenEdit,
    handleSavePlan,
    handleDeletePlan,
  };
}
